'use client';

import { Button, type ButtonProps, MetaRow, Tag, type TagProps } from '@lulalife/design-system';
import { Flag01 } from '@lulalife/design-system/icons/line';
import type { ReactNode, Ref } from 'react';
import { DSSurface } from './DSSurface';
import { SlideoutContentHeader } from './SlideoutContentHeader';

/**
 * The work order slideout's Overview body on DS parts (D3, decision D15-30): Figma "Foresight
 * Coordinator Baseline", `Slideout / Panel / Overview` (section 7686:9093). Field rows are DS
 * `MetaRow` (Split); Issues and Resident Availability sit under a Content Header with their alerts
 * and buttons. Every row keeps today's editor inside its value, so behaviour and test ids
 * stay as they were; only the frame around them is the DS's. The editors' dropdowns render into
 * `.wo-details-offcanvas`, outside these DS regions.
 */

/**
 * The field rows at the frame's 36 px pitch (20 px rows, 16 px apart). Today's editors keep their
 * 36 px-tall hover and click areas, so the rows themselves carry no gap, and the last area's extra
 * 8 px doesn't count toward the 24 px down to the next section.
 */
export const OverviewMetaRows = ({
  children,
  'data-testid': testId = 'work-order.overview.fields',
}: {
  children: ReactNode;
  'data-testid'?: string;
}) => (
  <DSSurface className="flex flex-col -mb-space-8" data-testid={testId}>
    {children}
  </DSSurface>
);

type OverviewMetaRowProps = {
  icon: ReactNode;
  label: string;
  /** The value: today's editor, in its own classes so it looks and behaves as before. */
  children: ReactNode;
  'data-testid'?: string;
};

/** One field: DS `MetaRow` (Split), the icon and key on the left, today's editor as the value. */
export const OverviewMetaRow = ({
  icon,
  label,
  children,
  'data-testid': testId,
}: OverviewMetaRowProps) => (
  <MetaRow
    layout="split"
    icon={icon}
    label={label}
    // Top-aligned, the key nudged down to sit on the first line of today's 36 px-tall editors
    // (6 px padding, 24 px line), so a value that wraps (an address and its market) reads right.
    // The value pulls back over the editors' 12 px side padding so its text sits on the frame's
    // value column while the hover area stays.
    // DS-GAP (local override): the icon's SVG measured 16 px on desktop while Figma's rows draw
    // 20, so the SVG is held at 20 here. Drop the last class when the package draws it.
    className="items-start [&>div:first-child]:pt-space-8 [&>div:last-child]:-ml-space-12 [&>div:first-child_svg]:size-[20px]"
    data-testid={testId}
    value={children}
  />
);

/**
 * `Status / Work Order`, which the DS package doesn't ship: a DS Tag in the status's tone (the
 * caller maps today's status colours to it, so every status keeps its colour family).
 */
export const OverviewStatusTag = ({
  label,
  color,
  icon,
  'data-testid': testId,
}: {
  label: string | null | undefined;
  color: TagProps['color'];
  icon?: TagProps['icon'];
  'data-testid': string;
}) =>
  label ? (
    <span className="inline-flex" data-testid={testId}>
      <Tag size="compact" color={color} label={label} icon={icon} />
    </span>
  ) : (
    <span data-testid={`${testId}-empty`} role="img" aria-label="No status">
      —
    </span>
  );

/** The priority as the frames draw it: a DS Tag with the flag. Urgent red; high and medium amber. */
export const OverviewPriorityTag = ({ label, value }: { label: string; value?: string }) => (
  <Tag
    size="compact"
    color={
      value === 'urgent' ? 'danger' : value === 'high' || value === 'medium' ? 'warning' : 'neutral'
    }
    icon={<Flag01 />}
    label={label}
  />
);

/** A block: its Content Header, then its alert, body and a row of buttons, 12 px apart. */
export const OverviewSection = ({
  title,
  alert,
  children,
  actions,
  headerActions,
  'data-testid': testId,
}: {
  title: string;
  alert?: ReactNode;
  children?: ReactNode;
  actions?: ReactNode;
  /** Buttons for the heading's own row (the Content Header's Right slot). */
  headerActions?: ReactNode;
  'data-testid'?: string;
}) => (
  <DSSurface className="flex w-full flex-col gap-space-12" data-testid={testId}>
    <SlideoutContentHeader title={title} right={headerActions ? <>{headerActions}</> : undefined} />
    {alert}
    {children}
    {actions ? <div className="flex flex-wrap items-center gap-space-8">{actions}</div> : null}
  </DSSurface>
);

/**
 * A DS Button as a reactstrap `DropdownToggle`'s `tag`: the toggle hands its Popper reference over
 * as `innerRef`, which lands on a wrapper, since DS Button takes no ref.
 */
export const DSDropdownToggleButton = ({
  innerRef,
  ...props
}: ButtonProps & { innerRef?: Ref<HTMLSpanElement> }) => (
  <span ref={innerRef} className="inline-flex">
    <Button type="button" variant="secondary" {...props} />
  </span>
);
